import moment from "moment";

import {
  DEFAULT_REPORT_RANGE_MONTHS,
  LARGE_REPORT_RANGE_MONTHS,
  formatReportRangeExceededMessage,
  isReportRangeMonths,
  REPORT_DATE_RANGE_EXCEEDED_CODE,
} from "src/constants/reportRange";

const DATE_FORMAT = "YYYY-MM-DD";

/**
 * True when a report range (dates as `YYYY-MM-DD`, local calendar days) is within `months` calendar months:
 * it may run up to the day before `fromDate` plus `months`. This is the backend rule (`toDate` must not
 * pass `fromDate` + N months), so 1 Jan to 31 Dec fits 12 months and 1 Jan to 1 Jan of the next year does not.
 * Month ends clamp (31 Jan + 1 month is 28 Feb), as on the backend.
 */
export const isRangeWithinLimit = (fromDate: string, toDate: string, months: number): boolean =>
  moment.utc(toDate, DATE_FORMAT).isBefore(moment.utc(fromDate, DATE_FORMAT).add(months, "months"));

/** True when the range is longer than `LARGE_REPORT_RANGE_MONTHS`: such a report can take a while, so the user is asked first. */
export const isLargeRange = (fromDate: string, toDate: string): boolean =>
  !isRangeWithinLimit(fromDate, toDate, LARGE_REPORT_RANGE_MONTHS);

/** Latest `toDate` for the given `fromDate`: the day before `fromDate` plus `months`. */
export const getMaxToDate = (fromDate: string, months: number): string =>
  moment.utc(fromDate, DATE_FORMAT).add(months, "months").subtract(1, "day").format(DATE_FORMAT);

/**
 * Earliest `fromDate` for the given `toDate`. Found by stepping forward from `toDate` minus `months`
 * until the range fits, because month-end clamping makes plain subtraction one to three days off.
 */
export const getMinFromDate = (toDate: string, months: number): string => {
  const candidate = moment.utc(toDate, DATE_FORMAT).subtract(months, "months");
  for (let step = 0; step < 5 && !isRangeWithinLimit(candidate.format(DATE_FORMAT), toDate, months); step += 1) {
    candidate.add(1, "day");
  }

  return candidate.format(DATE_FORMAT);
};

interface ErrorBody {
  code?: unknown;
  maxReportRangeMonths?: unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/** `Blob.text()`, with a `FileReader` fallback for environments that lack it. */
const readBlobText = (blob: Blob): Promise<string> => {
  if (typeof blob.text === "function") return blob.text();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
};

/** The response body of a failed request. A PDF request has `responseType: "blob"`, so its JSON error arrives as a Blob. */
const readErrorBody = async (error: unknown): Promise<ErrorBody | null> => {
  const data = isRecord(error) && isRecord(error.response) ? error.response.data : null;
  if (data instanceof Blob) {
    try {
      const parsed: unknown = JSON.parse(await readBlobText(data));
      return isRecord(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  return isRecord(data) ? data : null;
};

/**
 * Friendly text when a failed report request was rejected for its date range; `null` for any other error, so the
 * caller keeps its own message. Uses the limit sent back by the API, else `fallbackMonths`.
 */
export const getReportRangeErrorMessage = async (
  error: unknown,
  fallbackMonths: number = DEFAULT_REPORT_RANGE_MONTHS,
): Promise<string | null> => {
  const body = await readErrorBody(error);
  if (body?.code !== REPORT_DATE_RANGE_EXCEEDED_CODE) return null;

  const months = isReportRangeMonths(body.maxReportRangeMonths) ? body.maxReportRangeMonths : fallbackMonths;

  return formatReportRangeExceededMessage(months);
};
